import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, GripHorizontal, GripVertical } from 'lucide-react';
import { ElevationProfile } from './ElevationProfile';
import { colorByOptionViews } from '../../utils/colorByOptions';
import { useLayerReadiness } from '../../hooks/useLayerReadiness';
import { interpolateAlongCartesianPolyline } from '../../utils/polylineInterpolation';
import { useDialogResize } from '../../hooks/useDialogResize';
export const ElevationProfilePopout = ({ measurementId, elevationProfile, unit, color, colorBy, onColorByChange, onClose, onUnitChange, initialPosition, linePositions, onHoverPosition, externalCrosshairFraction }) => {
    const [localColorBy, setLocalColorBy] = useState('elevation');
    const effectiveColorBy = colorBy !== null && colorBy !== void 0 ? colorBy : (onColorByChange ? 'elevation' : localColorBy);
    const setEffectiveColorBy = (mode) => {
        if (onColorByChange)
            onColorByChange(mode);
        else
            setLocalColorBy(mode);
    };
    const layerState = useLayerReadiness();
    void measurementId; // referenced via props for keying upstream
    // ESC key to close
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);
    const [position, setPosition] = useState(() => {
        if (initialPosition) {
            return initialPosition;
        }
        // Default to center-ish of screen
        return {
            x: Math.max(50, window.innerWidth / 2 - 175),
            y: Math.max(50, window.innerHeight / 2 - 200)
        };
    });
    const { width, startLeftResize, startRightResize } = useDialogResize({
        initialWidth: 380,
        minWidth: 320,
        position,
        setPosition,
    });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const windowRef = useRef(null);
    const handleMouseDown = useCallback((e) => {
        if (windowRef.current) {
            const rect = windowRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setIsDragging(true);
        }
    }, []);
    const handleMouseMove = useCallback((e) => {
        if (isDragging) {
            const newX = Math.max(0, Math.min(window.innerWidth - width, e.clientX - dragOffset.x));
            const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
            setPosition({ x: newX, y: newY });
        }
    }, [isDragging, dragOffset, width]);
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);
    const handleHoverFraction = useCallback((fraction) => {
        if (!onHoverPosition || !linePositions || linePositions.length < 2)
            return;
        if (fraction === null) {
            onHoverPosition(null, color);
            return;
        }
        const pos = interpolateAlongCartesianPolyline(linePositions, fraction);
        onHoverPosition(pos, color);
    }, [onHoverPosition, linePositions, color]);
    return (_jsxs("div", { ref: windowRef, className: "fixed z-[65] rounded-lg shadow-2xl", style: {
            left: position.x,
            top: position.y,
            width,
            backgroundColor: 'var(--charcoal)',
            border: `2px solid ${color}`,
            userSelect: isDragging ? 'none' : 'auto'
        }, children: [_jsxs("div", { className: "flex items-center justify-between px-3 py-2 cursor-move rounded-t-lg", style: { backgroundColor: 'var(--pewter)' }, onMouseDown: handleMouseDown, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(GripHorizontal, { size: 14, style: { color: 'var(--dew)', opacity: 0.5 } }), _jsx("span", { className: "text-sm font-semibold", style: { color: 'var(--sand)' }, children: "Elevation Profile" })] }), _jsx("button", { onClick: onClose, className: "p-1 rounded hover:bg-opacity-20 hover:bg-gray-500 transition-colors", style: { color: 'var(--dew)' }, onMouseDown: (e) => e.stopPropagation(), children: _jsx(X, { size: 16 }) })] }), _jsxs("div", { className: "p-4", children: [_jsx(ElevationProfile, { elevationProfile: elevationProfile, unit: unit, color: color, colorBy: effectiveColorBy, onUnitChange: onUnitChange, onHoverFraction: linePositions && onHoverPosition ? handleHoverFraction : undefined, externalCrosshairFraction: externalCrosshairFraction }), _jsxs("div", { className: "mt-3", children: [_jsx("div", { className: "text-xs mb-1", style: { color: 'var(--dew)' }, children: "Color by" }), _jsx("select", { value: effectiveColorBy, onChange: (e) => setEffectiveColorBy(e.target.value), className: "w-full px-2 py-1 rounded text-xs cursor-pointer", style: {
                                    backgroundColor: 'var(--pewter)',
                                    color: 'var(--sand)',
                                    border: '1px solid var(--charcoal)',
                                }, children: colorByOptionViews(layerState).map(opt => (_jsx("option", { value: opt.value, disabled: opt.disabled, children: opt.label }, opt.value))) })] })] }), _jsx("div", { onMouseDown: startLeftResize, title: "Drag to resize width", style: { position: 'absolute', left: 0, bottom: 0, width: 16, height: 16, cursor: 'ew-resize', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(GripVertical, { size: 12, style: { color: 'var(--dew)', opacity: 0.6 } }) }), _jsx("div", { onMouseDown: startRightResize, title: "Drag to resize width", style: { position: 'absolute', right: 0, bottom: 0, width: 16, height: 16, cursor: 'ew-resize', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(GripVertical, { size: 12, style: { color: 'var(--dew)', opacity: 0.6 } }) })] }));
};
